import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HeaderProfileBlock from '../sharedChild/headerImage/headerProfileBlock';
import PriceForm from './priceForm';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import apiServices from '../../services/service';
import { toast } from 'react-toastify';
import { getActiveOwners, getGridDataByModuleKey } from '../../redux/selector';
import { useDispatch, useSelector } from 'react-redux';
import { initValuePrice } from '../../shared/initials';
import { fetchDataAndUpdateStore, singleRowAddUpdateInStore } from '../../redux/actions';
import cancel from '../../../src/assets/img/crm_home/header/cancel.png';
import save from '../../../src/assets/img/crm_home/header/save.png';
import { validationSchemaPrice } from './priceValidationSchema';

export const AddPriceList = () => {
  const stateData = useSelector((state) => state);
  let storeLoading = isLoadingByKey(
    stateData,
    'revenueTypeList',
    'leadSourceList',
    'industryList',
    'timeZone'
  );
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const location = useLocation();
  const [formData, setFormData] = useState(initValuePrice);
  const [errormsg, setErrorMsg] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({});
  const [loading, setLoading] = useState(storeLoading);
  const navigate = useNavigate();
  const gridData = useSelector(getGridDataByModuleKey('priceSheetList'));
  const dispatch = useDispatch();
  const [imagefile, setImageFile] = useState(null);
  const [formEdited, setFormEdited] = useState(false);

  useEffect(() => {
    let updatedFormData = {
      ...formData,
      type: location.pathname.includes('createPrice') ? 'add' : 'edit',
    };
    if (location.pathname.includes('editPrice') && location.state) {
      updatedFormData = {
        ...location.state,
        type: 'edit',
      };
    }
    setFormData(updatedFormData);
  }, [location]);

  const handleCloseAddForm = () => {
    if (formData.from === 'view') {
      navigate(
        `/products/priceSheet/viewPriceSheet?id=${formData.id}&pageNo=${gridData?.inputPayload?.pageNumber}`
      );
    } else {
      navigate('/products/priceSheet');
    }
  };

  const handleUploadImage = async (imagefile) => {
    setImageFile(imagefile);
    setFormEdited(true);
  };

  const handleChange = (selectedOption, name) => {
    setSelectedOptions((prevState) => ({
      ...prevState,
      [name]: selectedOption,
    }));

    handleLookupDropDownChange(selectedOption.value, selectedOption, name);
  };

  const handleLookupDropDownChange = (selectedId, selectedOption, name) => {
    const selectedValue = selectedOption.label;
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));

    if (name === 'revenueTypeId') {
      setFormData({
        ...formData,
        revenueTypeId: selectedId,
        revenueType: selectedValue,
      });

      setFormEdited(true);
    } else if (name === 'leadSourceId') {
      setFormData({
        ...formData,
        leadSourceId: selectedId,
        leadSource: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'industryId') {
      setFormData({
        ...formData,
        industryId: selectedId,
        industry: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'ownerId') {
      setFormData({
        ...formData,
        ownerId: selectedId,
        owner: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'timeZoneId') {
      setFormData({
        ...formData,
        timeZoneId: selectedId,
        timeZone: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'priceSheetOwnerId') {
      setFormData({
        ...formData,
        priceSheetOwnerId: selectedId,
        priceSheetOwner: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'priceModelId') {
      setFormData({
        ...formData,
        priceModelId: selectedId,
        priceModel: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'currencyPriceId') {
      setFormData({
        ...formData,
        currencyPriceId: selectedId,
        currencyPrice: selectedValue,
      });
      setFormEdited(true);
    }
  };

  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    let convertedValue = value;

    // For numeric-only fields
    if (['quantityInDemand', 'quantityOrdered', 'quantityInStock'].includes(name)) {
      convertedValue = value.replace(/[^0-9]/g, '');
    }

    // For unitPrice
    if (['unitPrice'].includes(name)) {
      if (value === '') {
        convertedValue = null; // Send null instead of empty string
      } else {
        let sanitized = value.replace(/[^0-9.]/g, '');

        const parts = sanitized.split('.');
        if (parts.length > 2) {
          sanitized = parts[0] + '.' + parts[1];
        }

        if (parts.length === 2) {
          parts[1] = parts[1].slice(0, 2);
          sanitized = parts[0] + '.' + parts[1];
        }

        const parsed = parseFloat(sanitized);
        convertedValue = !isNaN(parsed) && /^\d+(\.\d{1,2})?$/.test(sanitized) ? parsed : sanitized;
      }
    }

    // Clear the error for this field
    setErrorMsg((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [name]: convertedValue,
    }));

    setFormEdited(true);
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target; // Use 'checked' for true/false value
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: checked, // Set the checkbox state as true/false
    }));
    setFormEdited(true);
  };

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      const sanitizedData = {
        ...formData,
      };

      await validationSchemaPrice.validate(sanitizedData, {
        abortEarly: false,
      });
      handleFormSubmit(sanitizedData);
    } catch (error) {
      setLoading(false);
      const fieldErrors = {};
      error.inner.forEach((err) => {
        fieldErrors[err.path] = err.message;
      });
      setErrorMsg(fieldErrors);
    }
  };

  const handleFormSubmit = (formData) => {
    if (formData.type === 'add') {
      apiServices
        .createPrice(formData)
        .then((response) => {
          if (response.status === 200) {
            const id = response.data;
            updateList('Price created successfully!',id);
            setLoading(false);
            handleCloseAddForm();
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })

        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);
          toast.error('An unexpected error occurred.', {
            position: 'top-right',
          });
          setLoading(false);
        });
    } else {
      //   editUploadImage(formData.id);
      apiServices
        .updatePrice(formData)
        .then((response) => {
          if (response.status === 200) {
            updateList('Price updated successfully!');
            setLoading(false);
            handleCloseAddForm();
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })

        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);
          toast.error('An unexpected error occurred.', {
            position: 'top-right',
          });
          setLoading(false);
        });
    }
  };

  const updateList = async (msg, id) => {
    setLoading(false);

    toast.success(msg, {
      position: 'top-right',
    });

    const columnName = 'prodcutName';
    const orderType = 'desc';
    const data = {
      pageSize: parseInt(process.env.REACT_APP_DEFAULT_PAGESIZE),
      pageNumber: 1,
      columnName,
      orderType,
      filterJson: null,
    };

    try {
      // ✅ Await the dispatch before navigation
      // await dispatch(
      //   fetchDataAndUpdateStore({ key: "priceSheetList", inputData: data })
      // );
      dispatch(
        singleRowAddUpdateInStore({
          moduleName: 'PriceSheet',
          moduleKey: 'priceSheetList',
          id: formData.type === 'add' ? id : formData.id,
          rowIndex: formData.rowIndex,
        })
      );
      if (formData.from === 'view') {
        navigate(
          `/products/PriceSheet/viewPriceSheet?id=${formData.id}`
        );
      } else {
        navigate('/products/PriceSheet');
      }
    } catch (error) {
      toast.error('Something went wrong while updating the list.', {
        position: 'top-right',
      });
    }
  };

  return (
    <>
      <div className="inner-page" style={containerStyle}>
        <div id="product_page">
          <div className="viewHeader viewshadow">
            <HeaderProfileBlock
              localData={formData}
              moduleName={'PriceSheet'}
              uploadFile={handleUploadImage}
            />
            <div className="dsk">
              <div className="rightAction">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  data-bs-dismiss="modal"
                  onClick={handleCloseAddForm}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    handleSubmit(formData);
                  }}
                  disabled={loading || (formData.type === 'edit' && !formEdited)}
                >
                  {loading && (
                    <>
                      {' '}
                      <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                    </>
                  )}
                  {loading ? 'Loading...' : 'Save'}
                </button>
              </div>
            </div>
            <div className="mob">
              <div className="rightAction">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  data-bs-dismiss="modal"
                  onClick={handleCloseAddForm}
                >
                  <img src={cancel} alt="Cancel" style={{ width: '12px', height: 'auto' }} />
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    handleSubmit(formData);
                  }}
                  disabled={loading || (formData.type === 'edit' && !formEdited)}
                >
                  {loading ? (
                    <>
                      {' '}
                      <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                    </>
                  ) : (
                    <img src={save} alt="Save" style={{ width: '13px', height: 'auto' }} />
                  )}
                </button>
              </div>
            </div>
          </div>
          <PriceForm
            formData={formData}
            selectedOptions={selectedOptions}
            handleSubmit={handleSubmit}
            handleChangeForm={handleChangeForm}
            handleChangeCheckbox={handleChangeCheckbox}
            handleChange={handleChange}
            errormsg={errormsg}
          />
        </div>
      </div>
    </>
  );
};

export default AddPriceList;
